import React, { useState, useEffect } from 'react';
import { CheckCircleOutlined, FileTextOutlined, CloseOutlined } from '@ant-design/icons';

const DocuSignSuccessUI = () => {
  const [countdown, setCountdown] = useState(5);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Countdown timer
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Auto close window when countdown reaches 0
      setTimeout(() => {
        window.close();
      }, 500);
    }
  }, [countdown]);

  const handleManualClose = () => {
    window.close();
  };

  return (
    <div className="success-container">
      <div className={`success-card ${isVisible ? 'fade-in' : ''}`}>
        {/* Success Icon */}
        <div className="success-icon-container">
          <CheckCircleOutlined className="success-icon pulse" />
        </div>

        {/* Success Message */}
        <div className="success-content">
          <h1 className="success-title">Signature Submitted Successfully!</h1>
          <p className="success-subtitle">
            Your document has been signed and the contract has been created.
          </p>
          
          {/* Contract Icon */}
          <div className="contract-icon-container">
            <FileTextOutlined className="contract-icon" />
            <span className="contract-text">Contract Created</span>
          </div>
        </div>

        {/* Auto Close Info */}
        <div className="auto-close-info">
          <p className="countdown-text">
            This window will close automatically in <span className="countdown-number">{countdown}</span> seconds
          </p>
          {/* <button 
            onClick={handleManualClose}
            className="close-button"
            aria-label="Close window"
          >
            <CloseOutlined />
            Close Now
          </button> */}
        </div>

        {/* Decorative Elements */}
        <div className="confetti-container">
          <div className="confetti confetti-1"></div>
          <div className="confetti confetti-2"></div>
          <div className="confetti confetti-3"></div>
          <div className="confetti confetti-4"></div>
          <div className="confetti confetti-5"></div>
          <div className="confetti confetti-6"></div>
        </div>
      </div>

      <style jsx>{`
        .success-container {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .success-card {
          background: white;
          border-radius: 20px;
          padding: 50px 30px;
          text-align: center;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
          max-width: 500px;
          width: 100%;
          position: relative;
          transform: translateY(20px);
          opacity: 0;
          transition: all 0.6s ease;
        }

        .success-card.fade-in {
          transform: translateY(0);
          opacity: 1;
        }

        .success-icon-container {
          margin-bottom: 30px;
        }

        .success-icon {
          font-size: 80px;
          color: #52c41a;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .success-content {
          margin-bottom: 40px;
        }

        .success-title {
          font-size: 28px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 12px;
          margin-top: 0;
        }

        .success-subtitle {
          font-size: 16px;
          color: #6b7280;
          margin-bottom: 30px;
          line-height: 1.5;
        }

        .contract-icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: #f0f9ff;
          border: 2px solid #e0f2fe;
          border-radius: 12px;
          padding: 16px 24px;
          margin: 20px 0;
        }

        .contract-icon {
          font-size: 24px;
          color: #0369a1;
        }

        .contract-text {
          font-size: 16px;
          font-weight: 500;
          color: #0369a1;
        }

        .auto-close-info {
          border-top: 1px solid #e5e7eb;
          padding-top: 30px;
          margin-top: 30px;
        }

        .countdown-text {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 20px;
        }

        .countdown-number {
          font-weight: 600;
          color: #dc2626;
          font-size: 16px;
        }

        .close-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
          margin: 0 auto;
        }

        .close-button:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .close-button:active {
          transform: translateY(1px);
        }

        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          background: #fbbf24;
          animation: confetti-fall 3s linear infinite;
        }

        .confetti-1 {
          left: 10%;
          animation-delay: 0s;
          background: #ef4444;
        }

        .confetti-2 {
          left: 20%;
          animation-delay: 0.5s;
          background: #10b981;
        }

        .confetti-3 {
          left: 30%;
          animation-delay: 1s;
          background: #3b82f6;
        }

        .confetti-4 {
          left: 70%;
          animation-delay: 1.5s;
          background: #8b5cf6;
        }

        .confetti-5 {
          left: 80%;
          animation-delay: 2s;
          background: #f59e0b;
        }

        .confetti-6 {
          left: 90%;
          animation-delay: 2.5s;
          background: #ec4899;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .success-container {
            padding: 15px;
          }
          
          .success-card {
            padding: 40px 30px;
          }
          
          .success-title {
            font-size: 24px;
          }
          
          .success-icon {
            font-size: 60px;
          }
        }
      `}</style>
    </div>
  );
};

export default DocuSignSuccessUI;